import React, { useState, useEffect, useRef } from 'react';
import { useTeam } from '../../lib/teamConfig';
import gsap from 'gsap';

/**
 * Sport-Aware Password Gate Component
 *
 * Always displays SLAM logo for branding consistency
 * Sport-specific colors are applied via team config
 *
 * @param {Object} props
 * @param {React.ReactNode} props.children - Child components to render when authenticated
 * @param {boolean} [props.animateFromSportSelector=false] - If true, animate entry from SportSelector (emoji flies in)
 * @returns {JSX.Element}
 */
const PasswordGate = ({ children, animateFromSportSelector = false, gatePassword, gateStorageKey }) => {
  const { config, isLoading: teamLoading } = useTeam();
  const [password, setPassword] = useState('');
  const [isUnlocked, setIsUnlocked] = useState(false);
  const [isChecking, setIsChecking] = useState(true);
  const [error, setError] = useState('');
  const [isShaking, setIsShaking] = useState(false);
  const [mousePosition, setMousePosition] = useState({ x: 50, y: 50 });
  const [showCard, setShowCard] = useState(!animateFromSportSelector);
  /** @type {React.MutableRefObject<HTMLDivElement|null>} */
  const containerRef = useRef(null);
  /** @type {React.MutableRefObject<HTMLDivElement|null>} */
  const cardRef = useRef(null);

  const correctPassword = gatePassword || 'hes not here';
  const storageKey = gateStorageKey || 'slam-authenticated';

  // Determine sport based on team config
  const isBasketball = config?.features?.custom?.nbaTicker === true || config?.id === 'slamball';

  // Sport-specific theming - use team colors when available
  const primaryColor = config?.colors?.primary || (isBasketball ? '#FF6B00' : '#E8B800');
  const secondaryColor = config?.colors?.secondary || (isBasketball ? '#E55A00' : '#1a1a2e');

  // Use team branding if available, otherwise fallback to sport defaults
  const sportName = config?.branding?.tagline || config?.branding?.name || (isBasketball ? 'SLAM BALL' : 'SLAM TIPPING');
  const brandName = config?.branding?.name || 'SLAM Sports AI';

  // Check localStorage on mount
  useEffect(() => {
    const authenticated = localStorage.getItem(storageKey);
    if (authenticated === 'true') {
      setIsUnlocked(true);
    }
    setIsChecking(false);
  }, []);

  // Mouse tracking for gradient effect
  useEffect(() => {
    /**
     * @param {MouseEvent} e
     */
    const handleMouseMove = (e) => {
      if (containerRef.current) {
        const rect = containerRef.current.getBoundingClientRect();
        const x = ((e.clientX - rect.left) / rect.width) * 100;
        const y = ((e.clientY - rect.top) / rect.height) * 100;
        setMousePosition({ x, y });
      }
    };

    document.addEventListener('mousemove', handleMouseMove);
    return () => document.removeEventListener('mousemove', handleMouseMove);
  }, []);

  // Handle animation entry from SportSelector
  useEffect(() => {
    if (animateFromSportSelector && cardRef.current && !isChecking && !teamLoading) {
      // Small delay to let the page render
      const timer = setTimeout(() => {
        setShowCard(true);
        gsap.fromTo(
          cardRef.current,
          { opacity: 0, y: 30 },
          { opacity: 1, y: 0, duration: 0.5, ease: 'power2.out' }
        );
      }, 150);
      return () => clearTimeout(timer);
    }
  }, [animateFromSportSelector, isChecking, teamLoading]);

  /**
   * @param {React.FormEvent} e
   */
  const handleSubmit = (e) => {
    e.preventDefault();

    if (password.toLowerCase() === correctPassword.toLowerCase()) {
      setError('');
      localStorage.setItem(storageKey, 'true');
      setIsUnlocked(true);
    } else {
      setError('Incorrect password. Try again.');
      setIsShaking(true);
      setTimeout(() => setIsShaking(false), 500);
      setPassword('');
    }
  };

  // Show nothing while checking localStorage or team config
  if (isChecking || teamLoading) {
    return (
      <div className="fixed inset-0 bg-[#0a0a0c] flex items-center justify-center">
        <div
          className="w-8 h-8 border-2 border-t-transparent rounded-full animate-spin"
          style={{ borderColor: primaryColor, borderTopColor: 'transparent' }}
        />
      </div>
    );
  }

  // Show children if unlocked
  if (isUnlocked) {
    return <>{children}</>;
  }

  /**
   * SLAM Logo Component
   * Always displays SLAM logo for brand consistency
   */
  const SlamLogoIcon = () => (
    <div
      className="w-full h-full flex items-center justify-center"
      style={{
        filter: `drop-shadow(0 25px 50px ${primaryColor}66)`
      }}
    >
      <img
        src="/SSAILogo.svg"
        alt="SLAM Sports AI"
        className="w-4/5 h-4/5 object-contain animate-pulse-slow"
      />
    </div>
  );

  return (
    <div
      ref={containerRef}
      className="fixed inset-0 bg-[#0a0a0c] flex items-center justify-center overflow-hidden z-[9999]"
    >
      {/* Dynamic gradient background */}
      <div
        className="absolute inset-0 transition-all duration-300"
        style={{
          background: `radial-gradient(circle at ${mousePosition.x}% ${mousePosition.y}%, ${primaryColor}1F 0%, rgba(10, 10, 12, 1) 50%)`
        }}
      />

      {/* Grid pattern */}
      <div
        className="absolute inset-0 opacity-20"
        style={{
          backgroundImage: `linear-gradient(${primaryColor}14 1px, transparent 1px), linear-gradient(90deg, ${primaryColor}14 1px, transparent 1px)`,
          backgroundSize: '60px 60px'
        }}
      />

      {/* Floating particles */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        {[...Array(15)].map((_, i) => (
          <div
            key={i}
            className="absolute w-1 h-1 rounded-full opacity-20"
            style={{
              backgroundColor: primaryColor,
              left: `${10 + Math.random() * 80}%`,
              top: `${10 + Math.random() * 80}%`,
              animation: `float ${4 + Math.random() * 3}s ease-in-out infinite`,
              animationDelay: `${Math.random() * 2}s`
            }}
          />
        ))}
      </div>

      {/* Main content */}
      <div className="relative z-10 flex flex-col items-center gap-6 animate-fadeIn px-4">

        {/* SLAM Logo with lock overlay */}
        <div className="relative w-48 h-48 sm:w-56 sm:h-56 md:w-64 md:h-64">
          <SlamLogoIcon />

          {/* Lock icon overlay */}
          <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
            <div className="bg-black/40 backdrop-blur-sm rounded-full p-3 sm:p-4">
              <svg
                className="w-10 h-10 sm:w-12 sm:h-12 text-white drop-shadow-lg"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
                strokeWidth={2}
              >
                <rect x="3" y="11" width="18" height="11" rx="2" ry="2" />
                <path d="M7 11V7a5 5 0 0 1 10 0v4" />
              </svg>
            </div>
          </div>
        </div>

        {/* Card */}
        <div
          ref={cardRef}
          className={`
            bg-[#141418]/95 backdrop-blur-xl rounded-3xl p-6 sm:p-8 md:p-10
            border
            shadow-[0_20px_60px_rgba(0,0,0,0.5)]
            w-full max-w-[380px] sm:max-w-[420px]
            ${isShaking ? 'animate-shake' : ''}
            ${!showCard ? 'opacity-0' : ''}
          `}
          style={{
            borderColor: `${primaryColor}33`,
            boxShadow: `0 20px 60px rgba(0,0,0,0.5), 0 0 60px ${primaryColor}14`
          }}
        >
          {/* Branding */}
          <div className="text-center mb-6 sm:mb-8">
            <h1
              className="text-3xl sm:text-4xl md:text-5xl font-black tracking-wider bg-clip-text text-transparent"
              style={{
                backgroundImage: `linear-gradient(to right, ${primaryColor}, ${isBasketball ? '#FF8C40' : '#FFA500'}, ${primaryColor})`
              }}
            >
              {sportName}
            </h1>
            <p className="text-white/40 text-xs sm:text-sm tracking-[2px] sm:tracking-[3px] uppercase mt-2 font-medium">
              Enter Password to Continue
            </p>
          </div>

          {/* Form */}
          <form onSubmit={handleSubmit} className="flex flex-col gap-4 sm:gap-5">
            <div className="relative">
              {/* Lock icon in input */}
              <div className="absolute top-1/2 left-4 -translate-y-1/2" style={{ color: primaryColor }}>
                <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                  <rect x="3" y="11" width="18" height="11" rx="2" ry="2" />
                  <path d="M7 11V7a5 5 0 0 1 10 0v4" />
                </svg>
              </div>

              <input
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="PASSWORD"
                className="w-full py-3 sm:py-4 pl-12 pr-4 rounded-xl text-white text-sm sm:text-base font-medium tracking-widest placeholder:text-white/25 placeholder:tracking-[3px] sm:placeholder:tracking-[4px] focus:outline-none transition-all duration-300"
                style={{
                  backgroundColor: `${primaryColor}0D`,
                  borderWidth: '2px',
                  borderStyle: 'solid',
                  borderColor: error ? 'rgba(239, 68, 68, 0.7)' : `${primaryColor}4D`
                }}
                onFocus={(e) => {
                  e.target.style.borderColor = primaryColor;
                  e.target.style.backgroundColor = `${primaryColor}1A`;
                }}
                onBlur={(e) => {
                  e.target.style.borderColor = error ? 'rgba(239, 68, 68, 0.7)' : `${primaryColor}4D`;
                  e.target.style.backgroundColor = `${primaryColor}0D`;
                }}
                autoFocus
              />
            </div>

            {/* Error message */}
            {error && (
              <div className="flex items-center gap-2 text-red-400 text-xs sm:text-sm bg-red-500/10 border border-red-500/30 rounded-lg px-3 sm:px-4 py-2.5 sm:py-3">
                <svg className="w-4 h-4 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                  <circle cx="12" cy="12" r="10" />
                  <line x1="12" y1="8" x2="12" y2="12" />
                  <line x1="12" y1="16" x2="12.01" y2="16" />
                </svg>
                <span>{error}</span>
              </div>
            )}

            {/* Submit button */}
            <button
              type="submit"
              className="w-full py-3 sm:py-4 text-white text-sm sm:text-base font-bold tracking-[2px] sm:tracking-[3px] uppercase rounded-xl hover:-translate-y-0.5 active:translate-y-0 transition-all duration-300"
              style={{
                backgroundImage: `linear-gradient(to right, ${primaryColor}, ${secondaryColor})`,
                boxShadow: `0 4px 20px ${primaryColor}66`
              }}
              onMouseOver={(e) => {
                e.currentTarget.style.boxShadow = `0 6px 30px ${primaryColor}99`;
              }}
              onMouseOut={(e) => {
                e.currentTarget.style.boxShadow = `0 4px 20px ${primaryColor}66`;
              }}
            >
              Unlock
            </button>
          </form>
        </div>

        {/* Footer */}
        <p className="text-white/25 text-[10px] sm:text-xs tracking-widest uppercase">
          {brandName}
        </p>
      </div>

      {/* Keyframe animations */}
      <style>{`
        @keyframes float {
          0%, 100% { transform: translateY(0px); }
          50% { transform: translateY(-15px); }
        }
        @keyframes password-shake {
          0%, 100% { transform: translateX(0); }
          10%, 30%, 50%, 70%, 90% { transform: translateX(-8px); }
          20%, 40%, 60%, 80% { transform: translateX(8px); }
        }
        @keyframes password-fadeIn {
          from { opacity: 0; transform: scale(0.95); }
          to { opacity: 1; transform: scale(1); }
        }
        .animate-float {
          animation: float 3s ease-in-out infinite;
        }
        .animate-shake {
          animation: password-shake 0.5s ease-in-out;
        }
        .animate-fadeIn {
          animation: password-fadeIn 0.6s ease-out;
        }
        .animate-pulse-slow {
          animation: pulse 2.5s ease-in-out infinite;
        }
        @keyframes pulse {
          0%, 100% { transform: scale(1); opacity: 1; }
          50% { transform: scale(1.03); opacity: 0.9; }
        }
      `}</style>
    </div>
  );
};

export default PasswordGate;
